import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Anchor, Box, ThemeConstellation } from '@zillow/constellation';
import { DeferredRender, RenderMode } from '@zillow/react-deferred-hydration';
import type { SearchBox } from '@zillow/search-box';
import { useMobileState } from '@zillow/shopper-platform-config';

import ZILLOW_LOGO_SKUNKWORKS from '../../../public/images/z-logo-skunkworks.svg';
import { WRAPPER_SX } from '../../constants/styles';
import SearchBoxWrapper from '../SearchBoxWrapper/SearchBoxWrapper';

const useGetSearchBoxSize = (sticky: boolean) => {
    const isMobile = useMobileState();
    const [searchBoxSize, setSearchBoxSize] = useState<ComponentProps<typeof SearchBox>['size']>(
        isMobile ? 'lg' : 'xl'
    );

    const isLessThanMd = () =>
        typeof window !== 'undefined' &&
        window.innerWidth < ThemeConstellation.constellation.breakpoints.md;
    const [lessThanMd, setLessThanMd] = useState<boolean>(isLessThanMd());

    useEffect(() => {
        const onResize = () => {
            setLessThanMd(isLessThanMd());
        };

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        const newSize = (() => {
            if (sticky) {
                return 'md';
            }

            if (lessThanMd) {
                return 'lg';
            }

            return 'xl';
        })();

        setSearchBoxSize(newSize);
    }, [sticky, lessThanMd]);

    return searchBoxSize;
};

const StickySearchBox = () => {
    const searchBoxWrapperRef = useRef<HTMLDivElement>(null);

    const [sticky, setSticky] = useState(false);

    const searchBoxSize = useGetSearchBoxSize(sticky);

    const [originalPosition, setOriginalPosition] = useState({
        top: 0,
        height: 0,
    });

    const updateSticky = useCallback(() => {
        const { top, height } = originalPosition;

        if (!sticky && window.scrollY > top + height) {
            setSticky(true);
        } else if (sticky && window.scrollY < top) {
            setSticky(false);
        }
    }, [sticky, originalPosition]);

    useEffect(() => {
        window.addEventListener('scroll', updateSticky);
        updateSticky();

        return () => {
            window.removeEventListener('scroll', updateSticky);
        };
    }, [updateSticky]);

    const updateOriginalPosition = useCallback(() => {
        const element = searchBoxWrapperRef.current;

        if (!element || sticky) {
            return;
        }

        const top = element.getBoundingClientRect().top + window.scrollY;
        const height = element.offsetHeight;

        if (height !== originalPosition.height || top !== originalPosition.top) {
            setOriginalPosition({ top, height });
        }
    }, [sticky, searchBoxWrapperRef, originalPosition]);

    useEffect(() => {
        window.addEventListener('resize', updateOriginalPosition);
        updateOriginalPosition();

        return () => {
            window.removeEventListener('resize', updateOriginalPosition);
        };
    }, [updateOriginalPosition]);

    const smLte = '@media $sm_lte';
    const mdLte = '@media $md_lte';
    const lgLte = '@media $lg_lte';

    return (
        <Box
            sx={{
                ...(sticky && {
                    'zIndex': ThemeConstellation.constellation.zIndices.modal - 1,
                    'position': 'fixed',
                    'top': 0,
                    'left': 0,
                    'right': 0,
                    'backgroundColor': '$colors.backgroundWhite',
                    'borderBottom': '1px solid $colors.borderLight',
                    '[role=dialog]': {
                        // Value needed to show searchbox on top of the FPS navbar, see: https://zillowgroup.atlassian.net/browse/HOPS-78
                        zIndex: `${ThemeConstellation.constellation.zIndices.modal - 1} !important`,
                    },
                }),
            }}
        >
            <Box
                sx={{
                    gridTemplateColumns: '1fr',
                    display: 'grid',
                    gap: '$spacing.sm',
                    alignItems: 'center',
                    ...(sticky && {
                        'gridTemplateColumns': '1fr 668px 1fr',
                        '& form': {
                            'max-width': '100%',
                        },
                        ...WRAPPER_SX,
                        'paddingTop': '$spacing.sm',
                        'paddingBottom': '$spacing.sm',
                        'overflow': 'visible',
                        [lgLte]: {
                            gridTemplateColumns: '1fr 480px 1fr',
                        },
                        [mdLte]: {
                            gridTemplateColumns: '1fr',
                        },
                        [smLte]: {
                            paddingTop: '$spacing.xs',
                            paddingBottom: '$spacing.xs',
                        },
                    }),
                }}
            >
                {sticky && (
                    // Extra box to avoid Anchor taking the full width
                    <Box
                        sx={{
                            display: 'inline-block',
                            height: '30px',
                            [lgLte]: {
                                height: '22px',
                            },
                            [mdLte]: {
                                display: 'none',
                            },
                        }}
                    >
                        <Anchor role="link" href="/">
                            {/*  
                                TODO: Adding this new logo temporarily until Constellation 
                                    gets updated with the new one - probably in v10
                                    Once `ZillowLogo` is updated,
                                    remove ZILLOW_LOGO_SKUNKWORKS and use `ZillowLogo` 
                            */}
                            <img
                                src={ZILLOW_LOGO_SKUNKWORKS}
                                alt="logo"
                                style={{ background: 'transparent', height: '100%' }}
                            />
                        </Anchor>
                    </Box>
                )}
                <Box ref={searchBoxWrapperRef} data-testid="search-bar-container">
                    <DeferredRender mode={RenderMode.RENDER_SERVER_SIDE_HYDRATE_ON_IMMEDIATE}>
                        <SearchBoxWrapper size={searchBoxSize} />
                    </DeferredRender>
                </Box>
            </Box>
        </Box>
    );
};

export default StickySearchBox;
